import * as RadixToast from "@radix-ui/react-toast";
import { X, Check, AlertTriangle } from "lucide-react";
import { Heading, Text } from "./Typography";

export type ToastType = "success" | "error" | "info" | "warning";

interface ToastItemProps {
  type: ToastType;
  message: string;
  header?: string;
  duration: number;
  open: boolean;
  onClose: () => void;
}

const toastStyles: Record<ToastType, { bg: string; border: string; text: string }> = {
  success: {
    bg: "var(--theme-toast-success-bg)",
    border: "var(--theme-toast-success-border)",
    text: "var(--theme-toast-success-text)",
  },
  error: {
    bg: "var(--theme-toast-error-bg)",
    border: "var(--theme-toast-error-border)",
    text: "var(--theme-toast-error-text)",
  },
  info: {
    bg: "var(--theme-toast-info-bg)",
    border: "var(--theme-toast-info-border)",
    text: "var(--theme-toast-info-text)",
  },
  warning: {
    bg: "var(--theme-toast-warning-bg)",
    border: "var(--theme-toast-warning-border)",
    text: "var(--theme-toast-warning-text)",
  },
};

const ToastIcon = ({ type }: { type: ToastType }) => {
  const color = toastStyles[type].text;
  const strokeVar = "var(--theme-toast-icon-stroke)";

  if (type === "warning") {
    // Triangle shape — stroke defines both the outline and the inner "!"
    return (
      <AlertTriangle className="w-5 h-5 shrink-0" fill={color} stroke={strokeVar} strokeWidth={2} />
    );
  }

  // Circle icons: plain div circle (no border), inner symbol inherits currentColor
  const symbols = {
    success: <Check className="w-3 h-3" strokeWidth={3} />,
    error: <span className="text-[13px] font-bold leading-none select-none">!</span>,
    info: <span className="text-[12px] font-bold italic leading-none select-none">i</span>,
  };

  return (
    <div
      className="w-5 h-5 rounded-full shrink-0 flex items-center justify-center"
      style={{ background: color, color: strokeVar }}
    >
      {symbols[type]}
    </div>
  );
};

export const ToastItem = ({ type, message, header, duration, open, onClose }: ToastItemProps) => {
  const { bg, border, text } = toastStyles[type];

  return (
    <RadixToast.Root
      open={open}
      duration={duration}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 8,
        boxShadow: "var(--theme-toast-shadow)",
      }}
      className="relative w-80 data-[state=open]:toast-enter data-[state=closed]:toast-exit"
    >
      <div className="flex items-center gap-3 px-4 py-3 pr-10">
        <div className="flex items-center justify-center shrink-0">
          <ToastIcon type={type} />
        </div>

        <div className="flex flex-col min-w-0">
          {header && (
            <Heading size="6" as="h1" className="font-semibold text-[15px] leading-snug">
              <span style={{ color: text }}>{header}</span>
            </Heading>
          )}
          <Text size="4" as="p" className="text-[15px] leading-snug">
            <span style={{ color: text }}>{message}</span>
          </Text>
        </div>
      </div>

      <RadixToast.Close
        className="absolute right-3 top-3 rounded-full p-1 transition-colors hover:bg-black/5"
        onClick={onClose}
      >
        <X className="w-3.5 h-3.5" style={{ color: text }} />
      </RadixToast.Close>
    </RadixToast.Root>
  );
};
